import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { parseAndValidateAmount, formatAmount } from '../utils/amountParser';

interface DebouncedCustomAmountInputProps {
  initialValue: number | null | undefined;
  onUpdate: (value: number | null) => Promise<void>;
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
}

const DebouncedCustomAmountInput: React.FC<DebouncedCustomAmountInputProps> = ({
  initialValue,
  onUpdate,
  placeholder = "Override",
  style,
  className
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastSavedValue = useRef<number | null>(initialValue ?? null);

  const initialStringValue = useMemo(
    () => (initialValue === null || initialValue === undefined ? '' : initialValue.toString()),
    [initialValue]
  );

  const [localValue, setLocalValue] = useState(initialStringValue);
  const [isTyping, setIsTyping] = useState(false);
  const debouncedValue = useDebounce(localValue, 800);

  // Handle debounced updates
  useEffect(() => {
    const updateValue = async () => {
      // Skip if still typing or if it's the initial render
      if (isTyping || debouncedValue === initialStringValue) {
        return;
      }

      // Parse and validate the value using our utility
      const { value: numericValue, isValid, error: validationError } = parseAndValidateAmount(debouncedValue);
      
      // Only update if the value has actually changed
      if (numericValue === lastSavedValue.current) {
        return;
      }

      // Check validation
      if (!isValid) {
        setError(validationError || 'Invalid amount');
        return;
      }

      setError(null);
      setIsSaving(true);

      try {
        await onUpdate(numericValue);
        lastSavedValue.current = numericValue;
      } catch (error) {
        console.error('Error updating custom amount:', error);
        setError('Failed to update');
      } finally {
        setIsSaving(false);
        setIsTyping(false);
      }
    };

    updateValue();
  }, [debouncedValue, isTyping, onUpdate, initialStringValue]);

  // Update local value when initial value changes externally
  useEffect(() => {
    const newStringValue = initialValue === null || initialValue === undefined ? '' : initialValue.toString();
    // Only update if the value is different and we're not currently typing
    if (newStringValue !== localValue && !isTyping && !isSaving) {
      setLocalValue(newStringValue);
      lastSavedValue.current = initialValue ?? null;
    }
  }, [initialValue, localValue, isTyping, isSaving]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string, numbers, decimal point, and comma
    if (inputValue === '' || /^\d*[.,]?\d*$/.test(inputValue)) {
      setLocalValue(inputValue);
      setIsTyping(true);
      setError(null);
    }
  };

  const handleBlur = () => {
    // Format the value on blur if it's a valid number
    if (localValue !== '') {
      const { value: parsed, isValid } = parseAndValidateAmount(localValue);
      if (isValid && parsed !== null) {
        const formatted = formatAmount(parsed, 2);
        setLocalValue(formatted);
      }
    }
    setIsTyping(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent non-numeric characters except decimal point and comma
    if (!/[\d.,]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
      e.preventDefault();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        style={{
          width: '100px',
          padding: '4px 24px 4px 4px',
          borderColor: error ? '#dc3545' : undefined,
          ...style
        }}
        className={className}
        disabled={isSaving}
      />
      
      {/* Status indicator */}
      <div style={{
        position: 'absolute',
        right: '4px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '12px',
        pointerEvents: 'none'
      }}>
        {isTyping && !isSaving && (
          <span style={{ color: '#6c757d' }} title="Typing...">⏱</span>
        )}
        {isSaving && (
          <span style={{ color: '#007bff' }} title="Saving...">⏳</span>
        )}
        {!isTyping && !isSaving && !error && localValue !== '' && (
          <span style={{ color: '#28a745' }} title="Saved">✓</span>
        )}
        {error && (
          <span style={{ color: '#dc3545' }} title={error}>✗</span>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          fontSize: '12px',
          color: '#dc3545',
          marginTop: '2px',
          whiteSpace: 'nowrap'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default DebouncedCustomAmountInput;